import { useEffect, useState, useRef } from "react";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";
import useChartResize from "../hooks/useChartResize";
import useChartRender from "../hooks/useChartRender";
import useCustomiseLabels from "../hooks/useCustomiseLabels";
import useRenderWatermarkLabels from "../hooks/useRenderWatermarkLabels";
import useTeamCompetitivePositioningHandlers from "./useTeamCompetitivePositioningHandlers";
import useTeamCompetitiveAxes from "../hooks/useTeamCompetitiveAxes";

// Config
import { getOption } from "./TeamCompetitivePositioningConfig";

// Types
import { IBaseChartProps, IChartAxisTitle, IRefObjectForHighchartsReact } from "components/Charts/chart.types";
import { TUseTeamCompetitivePositioning } from "./TeamCompetitivePositioning.types";
import { LD4Chart } from "store/slice/charts/chartsSlice.types";

const useTeamCompetitivePositioning = ({
    width,
    height,
    getData,
    fromRefinement,
    moreOptions,
}: IBaseChartProps): TUseTeamCompetitivePositioning => {
    const chartData = useChartSlice(getData) as LD4Chart;
    const chartAxesTitles = useRef<IChartAxisTitle[]>([]);
    const chartRef = useRef<IRefObjectForHighchartsReact>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedSeriesIdxs = useRef<number[]>(
        chartData.series.reduce((acc: number[], serie, index: number) => (serie.visible ? [...acc, index] : acc), [])
    );
    const { renderWatermarkLabels } = useRenderWatermarkLabels(chartData);
    const { renderAxesTitles, setTitlesVisible } = useTeamCompetitiveAxes(
        chartAxesTitles,
        chartData,
        chartRef,
        "teamsCompetitivePositioning"
    );
    const [showRoles, setShowRoles] = useState(true);
    const [showAllLabels, setShowAllLabels] = useState(false);
    const [hideAllLabels, setHideAllLabels] = useState(false);

    const [{ load, personDataElement, peopleDataElement }, { onClose, onPointClick, legendItemClick }] =
        useTeamCompetitivePositioningHandlers(
            chartData,
            chartAxesTitles,
            renderWatermarkLabels,
            selectedSeriesIdxs,
            containerRef
        );

    const [chartOptions, setChartOptions] = useState(
        getOption(
            { onClose, load, legendItemClick, onPointClick },
            chartData,
            setTitlesVisible,
            moreOptions?.hideLegend
        )
    );

    useCustomiseLabels(showRoles, setChartOptions, selectedSeriesIdxs.current, showAllLabels, hideAllLabels, true);

    const { chartExtraOptions, resizeReRender, onChartResize } = useChartResize(width, height);

    useEffect(() => {
        if (fromRefinement) setShowRoles(false);
    }, [fromRefinement]);

    useEffect(() => {
        const chart = chartRef?.current?.chart;
        if (!chart?.renderer) return;

        setChartOptions((currentValue) => {
            const { chart, credits, legend, series, ...currentValueRest } = currentValue;

            return {
                ...currentValueRest,
                chart: {
                    ...chart,
                    ...chartExtraOptions.current?.chart,
                },
                credits: {
                    ...credits,
                    position: {
                        ...credits?.position,
                        ...chartExtraOptions.current?.credits?.position,
                    },
                },
                legend: {
                    ...legend,
                    ...chartExtraOptions.current?.legend,
                },
                series: series?.map(
                    (serie, index) =>
                        ({
                            ...serie,
                            visible: selectedSeriesIdxs.current.includes(index) ? true : false,
                        } ?? chartData.series)
                ),
            };
        });
    }, [chartData, chartExtraOptions, resizeReRender, width, height]);

    // Chart Render
    useChartRender(width, height, chartRef, [onChartResize, renderAxesTitles, renderWatermarkLabels]);

    return [
        { chartOptions, chartData, chartRef, personDataElement, peopleDataElement, containerRef, showRoles },
        { onClose, setShowRoles, showAllLabels, setShowAllLabels, hideAllLabels, setHideAllLabels },
    ];
};

export default useTeamCompetitivePositioning;
